import { Filter } from 'lucide-react';
import { type MouseEvent, type ReactElement, useCallback, useState } from 'react';

import { cn, Popover, PopoverContent, PopoverTrigger } from '@crossnokaye/ux-common';

import { useTable } from '@/components/data-table/core/context';

import {
  DateRangeFilter,
  MultiSelectFilter,
  NumberRangeFilter,
  SearchFilter,
  SelectFilter,
} from './filters';
import { useFilterState } from './hooks/use-filter-state';
import type { DateRangeValue, FilterTriggerProps, NumberRangeValue } from './types';

/**
 * Filter trigger button with popover for column filtering.
 * Renders in the column header when filterType is configured.
 */
export const FilterTrigger = <TData,>({
  header,
  filterType,
  filterOptions = [],
  filterPlaceholder,
}: FilterTriggerProps<TData>): ReactElement | null => {
  const { isMobile } = useTable<TData>();
  const [isOpen, setIsOpen] = useState(false);
  const columnId = header.column.id;

  const { value, setValue, clear, isActive } = useFilterState({
    columnId,
    filterType,
  });

  // Prevent click from triggering sort - must be before any early returns
  const handleTriggerClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Close handler for filter components - must be before any early returns
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Hide on mobile
  if (isMobile) {
    return null;
  }

  // Get default placeholder based on filter type
  const getDefaultPlaceholder = (type: string): string => {
    switch (type) {
      case 'search':
        return 'Search...';
      case 'select':
        return 'Select...';
      case 'multi-select':
        return 'Select options...';
      case 'date-range':
        return 'Select date range';
      case 'number-range':
        return 'Enter range';
      default:
        return '';
    }
  };

  // Render appropriate filter input based on type
  const renderFilterContent = () => {
    const placeholder = filterPlaceholder ?? getDefaultPlaceholder(filterType);

    switch (filterType) {
      case 'search':
        return (
          <SearchFilter
            columnId={columnId}
            value={value as string}
            onChange={setValue}
            placeholder={placeholder}
            onClear={clear}
            isActive={isActive}
            onClose={handleClose}
          />
        );
      case 'select':
        return (
          <SelectFilter
            columnId={columnId}
            value={value as string}
            onChange={setValue}
            options={filterOptions}
            placeholder={placeholder}
            onClear={clear}
            isActive={isActive}
            onClose={handleClose}
          />
        );
      case 'multi-select':
        return (
          <MultiSelectFilter
            columnId={columnId}
            value={value as string[]}
            onChange={setValue}
            options={filterOptions}
            placeholder={placeholder}
            onClear={clear}
            isActive={isActive}
            onClose={handleClose}
          />
        );
      case 'date-range':
        return (
          <DateRangeFilter
            columnId={columnId}
            value={value as DateRangeValue}
            onChange={setValue}
            onClear={clear}
            isActive={isActive}
            onClose={handleClose}
          />
        );
      case 'number-range':
        return (
          <NumberRangeFilter
            columnId={columnId}
            value={value as NumberRangeValue}
            onChange={setValue}
            onClear={clear}
            isActive={isActive}
            onClose={handleClose}
          />
        );
      default:
        return null;
    }
  };

  // Get appropriate width class based on filter type
  const getPopoverWidth = (): string => {
    switch (filterType) {
      case 'search':
      case 'select':
      case 'multi-select':
        return 'w-64'; // 256px
      case 'number-range':
        return 'w-80'; // 320px
      case 'date-range':
        return 'w-auto'; // Auto for calendar
      default:
        return 'w-64';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild onClick={handleTriggerClick} onMouseDown={(e) => e.stopPropagation()}>
        <Filter
          className={cn(
            'glyph glyph-sm hover:cursor-pointer',
            isActive ? 'text-primary' : 'text-foreground/50'
          )}
          data-testid={`filter-icon-${columnId}`}
          aria-label={`Filter ${header.column.id}`}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn(
          'p-0 rounded border-solid translate-x-2.5 translate-y-1 z-popout',
          getPopoverWidth()
        )}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        withPortal
      >
        {renderFilterContent()}
      </PopoverContent>
    </Popover>
  );
};

FilterTrigger.displayName = 'FilterTrigger';
